import { PencilIcon } from "@heroicons/react/outline";
import { useEffect, useMemo, useState } from "react";

import { InlineEdit } from "../..";
import { OverflowDisplayTooltip } from "../Tooltip/OverflowDisplayTooltip";

import { DescriptionProps } from "./types";

import { useRbac } from "@/providers/RbacProvider";
import { EntityType } from "@/queries/folder-entities/types";

const DESCRIPTION_PLACEHOLDER = "Add a description";

const ERROR_MESSAGE_DISMISS_MS = 5000;

const normalizeDescription = (value: string | null | undefined) => {
  const trimmed = (value ?? "").trim();
  return trimmed || null;
};

export const Description = ({ description, onDescriptionEdit, type }: DescriptionProps) => {
  const { isEditDisabled } = useRbac();
  const cannotEdit = isEditDisabled(type as EntityType);
  const normalizedDescription = useMemo(() => normalizeDescription(description), [description]);
  const [pendingDescription, setPendingDescription] = useState<string | null>(
    normalizedDescription,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setPendingDescription(normalizedDescription);
  }, [normalizedDescription]);

  useEffect(() => {
    if (!errorMessage) {
      return;
    }

    const timer = setTimeout(() => setErrorMessage(null), ERROR_MESSAGE_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  if ((cannotEdit || !onDescriptionEdit) && !normalizedDescription) {
    return null;
  }

  const handleEdit = (newDescription: string) => {
    setErrorMessage(null);

    const normalizedNewDescription = normalizeDescription(newDescription);
    if (normalizedNewDescription === normalizedDescription) {
      setErrorMessage("Description is unchanged.");
      setPendingDescription(normalizedDescription);
      return;
    }

    if (!onDescriptionEdit) {
      setPendingDescription(normalizedNewDescription);
      return;
    }

    setPendingDescription(normalizedNewDescription);
    setIsSaving(true);
    onDescriptionEdit({
      newDescription: normalizedNewDescription,
      onSuccess: nextDescription => {
        setPendingDescription(normalizeDescription(nextDescription));
        setIsSaving(false);
      },
      onError: () => {
        setPendingDescription(normalizedDescription);
        setErrorMessage("Failed to update description. Please try again.");
        setIsSaving(false);
      },
    });
  };

  const descriptionText = pendingDescription ?? DESCRIPTION_PLACEHOLDER;
  const descriptionClasses = pendingDescription
    ? "truncate text-sm text-gray-500"
    : "truncate text-sm italic text-gray-400";

  if (cannotEdit || !onDescriptionEdit) {
    return (
      <div className="mt-1 min-w-0 max-w-[50vw]">
        <OverflowDisplayTooltip content={descriptionText}>
          <p className={descriptionClasses}>{descriptionText}</p>
        </OverflowDisplayTooltip>
      </div>
    );
  }

  return (
    <div className="mt-1 min-w-0 max-w-[50vw]">
      <InlineEdit.Root
        initialValue={pendingDescription ?? ""}
        onEdit={handleEdit}
        onCancel={() => {
          setPendingDescription(normalizedDescription);
          setErrorMessage(null);
        }}
        readOnly={isSaving}
        className="min-w-0 justify-start p-0"
      >
        <InlineEdit.Trigger className="min-w-0">
          <OverflowDisplayTooltip content={descriptionText}>
            <p className={descriptionClasses}>{descriptionText}</p>
          </OverflowDisplayTooltip>
        </InlineEdit.Trigger>
        <InlineEdit.EditTrigger>
          <PencilIcon className="h-4 w-4" />
        </InlineEdit.EditTrigger>
        <InlineEdit.Input
          placeholder={DESCRIPTION_PLACEHOLDER}
          className="h-8 py-0 text-sm text-gray-600"
        />
        <InlineEdit.Rename>Save</InlineEdit.Rename>
        <InlineEdit.Cancel>Cancel</InlineEdit.Cancel>
      </InlineEdit.Root>
      {errorMessage && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
